import React from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cn } from "../utils"
import type { StrictOmit } from "../utils"

// =============================================================================
// Props
// =============================================================================

export type TabsPanelProps = {
  /** The content shown while the panel's tab is selected */
  children?: React.ReactNode
  /**
   * Layout classes, such as padding, or `min-w-0 flex-1` to fill the space
   * beside a vertical list
   */
  className?: string
  /** The value of the `Tabs.Tab` that shows this panel */
  value: TabsPrimitive.Tab.Value
  /**
   * Whether to keep the panel mounted, and hidden, while its tab isn't
   * selected. Use it to keep a panel's state, such as uncontrolled form
   * fields, when switching tabs.
   */
  keepMounted?: boolean
} & StrictOmit<
  React.ComponentPropsWithoutRef<"div">,
  // Owned by the panel, or wired up by Base UI
  "children" | "className" | "role" | "tabIndex" | "hidden" | "aria-labelledby"
>

// =============================================================================
// Styles
// =============================================================================

const tabsPanelStyles =
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900"

// =============================================================================
// Component
// =============================================================================

/**
 * The content for a `Tabs.Tab`, shown while the tab with the same `value` is
 * selected.
 *
 * Must be a descendant of `Tabs`.
 */
export const TabsPanel = React.forwardRef<HTMLDivElement, TabsPanelProps>(
  function TabsPanel(
    { children, className, value, keepMounted = false, ...props },
    ref
  ) {
    return (
      <TabsPrimitive.Panel
        {...props}
        ref={ref}
        value={value}
        keepMounted={keepMounted}
        className={cn(tabsPanelStyles, className)}
        data-slot="tabs-panel"
      >
        {children}
      </TabsPrimitive.Panel>
    )
  }
)

TabsPanel.displayName = "Tabs.Panel"
